<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';
// BonBoZ: mau chu doc duoc tren nen nhan, dung chung voi luoi tag chat.
import { mauChuTrenNen } from 'dashboard/helper/labelContrast';

// Nen mang mau cua nhan, chu lay mau doc duoc tren chinh nen do. Tra ca hai
// cung mot cho de khong noi nao lo dat nen ma quen dat chu.
const styleChip = hex => ({ backgroundColor: hex, color: mauChuTrenNen(hex) });

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});
const searchQuery = ref('');
// BonBoZ: xoa nhieu the mot lan. Giu ID chu khong giu ca doi tuong: danh sach
// nhan duoc nap lai sau moi lan xoa, giu doi tuong la giu ban sao cu.
const daChon = ref(new Set());
const showBulkDeletePopup = ref(false);
const dangXoaNhieu = ref(false);

const records = computed(() => getters['labels/getLabels'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'title', weight: 4 },
    'description',
  ]);
});
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

// ── Chon nhieu ───────────────────────────────────────────────────────────────
// Danh sach the DANG DUOC CHON, doc nguoc tu `records` chu khong giu rieng: the
// nao vua bi xoa hoac bi loc mat thi tu roi khoi day, khong con ID mo coi.
const nhanDaChon = computed(() =>
  records.value.filter(nhan => daChon.value.has(nhan.id))
);

const daChonHet = computed(
  () =>
    filteredRecords.value.length > 0 &&
    filteredRecords.value.every(nhan => daChon.value.has(nhan.id))
);

// Vach ngang (indeterminate) khi chon mot phan: o tick chi co hai trang thai
// nhin thay duoc, ma "mot vai cai" la trang thai thu ba — thieu no thi o tick
// hien trong roi, nguoi dung tuong chua chon gi.
const chonMotPhan = computed(
  () =>
    !daChonHet.value &&
    filteredRecords.value.some(nhan => daChon.value.has(nhan.id))
);

const doiChon = (id, bat) => {
  const moi = new Set(daChon.value);
  if (bat) moi.add(id);
  else moi.delete(id);
  daChon.value = moi;
};

// CHON HET chi cham toi nhung the DANG HIEN. O tick nam ngay tren danh sach da
// loc, nen no phai noi ve danh sach do — quet ca nhung the dang bi tu khoa tim
// kiem giau di la thu de xoa nham nhat.
const doiChonHet = bat => {
  const moi = new Set(daChon.value);
  filteredRecords.value.forEach(nhan => {
    if (bat) moi.add(nhan.id);
    else moi.delete(nhan.id);
  });
  daChon.value = moi;
};

const boChon = () => {
  daChon.value = new Set();
};

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

const openBulkDeletePopup = () => {
  showBulkDeletePopup.value = true;
};
const closeBulkDeletePopup = () => {
  showBulkDeletePopup.value = false;
};

// Xoa tung cai mot: store chi co `labels/delete(id)`, khong co duong xoa hang
// loat o API. Dem rieng so hong thay vi bao "xong" — the dang duoc gan o hoi
// thoai nao do co the bi tu choi, ma bao thanh cong het thi nguoi dung khong
// bao gio biet cai nao con lai.
const xoaNhieu = async () => {
  const canXoa = nhanDaChon.value;
  closeBulkDeletePopup();
  dangXoaNhieu.value = true;
  let ok = 0;
  let loi = 0;
  // TUAN TU chu khong `Promise.all`: 23 the la 23 request bay cung luc, may chu
  // de tra 429, va khi mot cai hong thi khong biet cai nao. Xep hang bang
  // `reduce` tren promise vi eslint cua repo cam vong `for...of`.
  await canXoa.reduce(
    (hangDoi, nhan) =>
      hangDoi.then(async () => {
        try {
          await store.dispatch('labels/delete', nhan.id);
          ok += 1;
        } catch {
          loi += 1;
        }
      }),
    Promise.resolve()
  );
  dangXoaNhieu.value = false;
  boChon();
  useAlert(
    loi
      ? t('BONBOZ.THE_CAI_DAT.KET_QUA_LOI', { ok, loi })
      : t('BONBOZ.THE_CAI_DAT.KET_QUA', { ok })
  );
};

const tableHeaders = computed(() => {
  return [
    '',
    t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.ACTION'),
  ];
});

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LABEL_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('LABEL_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('LABEL_MGMT.HEADER')"
        :description="$t('LABEL_MGMT.DESCRIPTION')"
        :link-text="$t('LABEL_MGMT.LEARN_MORE')"
        :search-placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="labels"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('LABEL_MGMT.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <!-- Nut xoa CHI hien khi da chon: mot nut xoa do luon nam san canh
               nut Them the la moi luc nao cung co mot cu bam nham cho san. -->
          <Button
            v-if="daChon.size"
            :label="$t('BONBOZ.THE_CAI_DAT.XOA_CHON', { n: daChon.size })"
            size="sm"
            ruby
            icon="i-woot-bin"
            :is-loading="dangXoaNhieu"
            @click="openBulkDeletePopup"
          />
          <Button
            v-if="daChon.size"
            :label="$t('BONBOZ.THE_CAI_DAT.BO_CHON')"
            size="sm"
            slate
            faded
            @click="boChon"
          />
          <Button
            :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery ? $t('LABEL_MGMT.NO_RESULTS') : $t('LABEL_MGMT.LIST.404')
        "
      >
        <template #header-0>
          <Checkbox
            :model-value="daChonHet"
            :indeterminate="chonMotPhan"
            :title="$t('BONBOZ.THE_CAI_DAT.CHON_HET')"
            @change="doiChonHet($event.target.checked)"
          />
        </template>
        <template #row="{ items }">
          <BaseTableRow v-for="label in items" :key="label.title" :item="label">
            <template #default>
              <BaseTableCell>
                <Checkbox
                  :model-value="daChon.has(label.id)"
                  @change="doiChon(label.id, $event.target.checked)"
                />
              </BaseTableCell>

              <BaseTableCell>
                <!-- BonBoZ: tieu de ve thanh CHIP mang dung mau cua nhan.
                     Truoc day tieu de la chu xam, con mau nam trong o vuong
                     16px va mot chuoi hex o cot khac - phai doi chieu ba chi
                     tiet moi biet nhan nay mau gi. Chip la thu nhan viec that
                     su nhin thay duoi khung chat, nen bang cai dat phai hien
                     dung hinh hai do. -->
                <span
                  class="inline-block px-2 py-0.5 text-body-main rounded-md"
                  :style="styleChip(label.color)"
                >
                  {{ label.title }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ label.description }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <!-- Ma mau van giu lai (co nguoi can chep ra), nhung ha
                     xuong chu mo: mau da nam o chip ben canh roi. -->
                <span class="font-mono text-body-main text-n-slate-10">
                  {{ label.color }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    :is-loading="loading[label.id]"
                    @click="openEditPopup(label)"
                  />
                  <Button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[label.id]"
                    @click="openDeletePopup(label)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <!-- LIET KE TEN tung the sap xoa chu khong chi noi so luong.
         O tick chon het chi quet danh sach dang hien, nhung lua chon van con
         nguyen khi doi tu khoa tim kiem — nen con so "5 the" khong du de biet
         la nam cai NAO. Doc ten ra thi het mo ho. -->
    <woot-delete-modal
      v-model:show="showBulkDeletePopup"
      :on-close="closeBulkDeletePopup"
      :on-confirm="xoaNhieu"
      :title="$t('BONBOZ.THE_CAI_DAT.XN_TIEU_DE', { n: daChon.size })"
      :message="$t('BONBOZ.THE_CAI_DAT.XN_MO_TA')"
      :message-value="` ${nhanDaChon.map(nhan => nhan.title).join(', ')}`"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>

<style lang="scss" scoped>
// O TICK CHUA CHON PHAI NHIN THAY DUOC.
//
// `Checkbox.vue` dung `appearance-none` + `border-n-slate-6` va KHONG dat nen.
// Tren mot hang bang nen trang thi do la vien xam nhat nhat tren nen trang —
// gan nhu vo hinh, khong ai biet la co cho de tick. O cac man khac no nam tren
// nen xam nen con thay duoc, o day thi khong.
//
// Vien `n-slate-9` (139 141 152) chu khong phai `n-slate-8` (185 187 198): mot
// duong 1px quanh o 16px thi bac 8 tren nen trang van chim.
// Nen `n-alpha-1` chu khong phai `n-solid-2`: DA THU `n-solid-2` va no vo dung
// hoan toan — trong theme sang bien do bang `255 255 255`, tuc la to trang len
// nen trang. `n-alpha-1` la mot lop den 5% nen no an theo nen ben duoi, sang
// hay toi deu ra dung mot lop xam nhat nhin thay duoc.
//
// `:not(:checked):not(:indeterminate)` la bat buoc: `:deep()` them thuoc tinh
// pham vi nen do uu tien cao hon class cua chinh component — khong loai tru thi
// luat nay de len ca `checked:bg-n-brand`, tick vao roi ma o van trang.
:deep(input[type='checkbox']:not(:checked):not(:indeterminate)) {
  @apply border-n-slate-9 bg-n-alpha-1;

  &:hover:enabled {
    @apply border-n-slate-11 bg-n-alpha-2;
  }
}
</style>
